import type { CustomRequestOptions } from '@/http/interceptor'

// 登录页路径就地定义，不引 @/utils 的 buildLoginUrl：http 层保持轻依赖（豁免说明见 src/utils/README.md）
const LOGIN_PAGE = '/pages/login/index'
// 不做业务码统一处理的接口白名单（如埋点上报等），命中则原样返回
const bizCodeWhiteList: string[] = []

/**
 * 统一请求封装。
 * - HTTP 2xx 且业务 code === 200 时 resolve(IResData)
 * - 其它情况一律 reject 并给出轻提示（不吞错误）
 * - 401 统一弹窗后跳转登录
 */
export function http<T>(options: CustomRequestOptions) {
  return new Promise<IResData<T>>((resolve, reject) => {
    uni.request({
      ...options,
      dataType: 'json',
      // #ifndef MP-WEIXIN
      responseType: 'json',
      // #endif
      success(res) {
        // 参考 axios，HTTP 状态码 2xx 视为成功
        if (res.statusCode >= 200 && res.statusCode < 300) {
          const resData = res.data as IResData<T>
          const url = options.url || ''
          const inWhiteList = bizCodeWhiteList.some(w => url.includes(w))
          // 业务码统一处理
          if (!inWhiteList && resData && typeof resData.code === 'number' && resData.code !== 200) {
            handleBizError(resData, options)
            reject(resData)
            return
          }
          resolve(resData)
        }
        else if (res.statusCode === 401) {
          handleUnauthorized()
          reject(res)
        }
        else {
          if (!options.hideErrorToast) {
            uni.showToast({
              icon: 'none',
              title: (res.data as IResData<T>)?.message || '请求错误',
            })
          }
          reject(res)
        }
      },
      fail(err) {
        if (!options.hideErrorToast) {
          uni.showToast({
            icon: 'none',
            title: '网络错误，换个网络试试',
          })
        }
        reject(err)
      },
    })
  })
}

function handleBizError<T>(resData: IResData<T>, options: CustomRequestOptions) {
  if (resData.code === 401) {
    handleUnauthorized()
    return
  }
  if (!options.hideErrorToast) {
    uni.showToast({
      icon: 'none',
      title: resData.message || '请求错误',
    })
  }
}

// 防止并发 401 弹出多个登录弹窗
let redirecting = false

// 取当前页完整路径（含 query），供登录后回跳
function getCurrentFullPath() {
  const pages = getCurrentPages()
  const page = pages[pages.length - 1] as any
  if (!page)
    return ''
  const opts = page.options as Record<string, string> | undefined
  const query = opts && Object.keys(opts).length > 0
    ? `?${Object.entries(opts).map(([k, v]) => `${k}=${v}`).join('&')}`
    : ''
  return `/${page.route}${query}`
}

/** 401 统一处理：弹窗后 reLaunch 跳登录页并带 redirect 回跳；普通请求与 SSE 共用 */
export function handleUnauthorized() {
  if (redirecting)
    return
  redirecting = true
  uni.showModal({
    title: '提示',
    content: '请先登录',
    showCancel: false,
    success() {
      const from = getCurrentFullPath()
      const url = from
        ? `${LOGIN_PAGE}?redirect=${encodeURIComponent(from)}`
        : LOGIN_PAGE
      // 登录态失效清空页面栈，回到登录页；登录后按 redirect 回跳
      uni.reLaunch({ url })
    },
    complete() {
      redirecting = false
    },
  })
}

/**
 * GET 请求
 * @param url 后台地址
 * @param query 请求 query 参数
 */
export function httpGet<T>(url: string, query?: Record<string, any>) {
  return http<T>({ url, query, method: 'GET' })
}

/**
 * POST 请求
 * @param url 后台地址
 * @param data 请求 body 参数
 * @param query 请求 query 参数（部分微信接口需要）
 */
export function httpPost<T>(url: string, data?: Record<string, any>, query?: Record<string, any>) {
  return http<T>({ url, query, data, method: 'POST' })
}

http.get = httpGet
http.post = httpPost
